import React from "react";
import { Route } from "lucide-react";
import { EuRelabelCheckInRoutingInstruction, EuRelabelCheckInRoutingType } from "src/types";
import { ROUTING_TYPE_CONFIG } from "src/constants/relabelCases";

const ROUTING_ORDER: EuRelabelCheckInRoutingType[] = ["UK_RELABEL", "UK_STANDARD", "EU_RELABEL", "EU_STANDARD"];

const STICKER_CALLOUT: Record<
  EuRelabelCheckInRoutingType,
  { colorLabel?: string; colorTextClass: string; lane: number }
> = {
  UK_RELABEL: { colorLabel: "BLUE", colorTextClass: "text-blue-600", lane: 1 },
  UK_STANDARD: { colorTextClass: "text-gray-600", lane: 2 },
  EU_RELABEL: { colorLabel: "GREEN", colorTextClass: "text-green-600", lane: 3 },
  EU_STANDARD: { colorLabel: "RED", colorTextClass: "text-red-600", lane: 4 }
};

interface RoutingInstructionCardsProps {
  routingInstructions: EuRelabelCheckInRoutingInstruction[];
  variant?: "default" | "checkInRouting";
}

const RoutingInstructionCards: React.FC<RoutingInstructionCardsProps> = ({
  routingInstructions,
  variant = "default"
}) => {
  const sortedInstructions = ROUTING_ORDER.filter((type) => routingInstructions.some((ri) => ri.type === type)).map(
    (type) => routingInstructions.find((ri) => ri.type === type)!
  );

  if (sortedInstructions.length === 0) {
    return null;
  }

  const totalUnits = sortedInstructions.reduce((sum, ri) => sum + ri.totalUnits, 0);

  return (
    <div className="rounded-2xl border border-secondary-200 bg-white shadow-sm overflow-hidden">
      {/* Section Header */}
      <div className="bg-black px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-white/10 p-2">
            <Route className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white tracking-wide">System-Calculated Routing Instructions</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {sortedInstructions.length} routing line{sortedInstructions.length !== 1 ? "s" : ""} &middot; {totalUnits}{" "}
              units total
            </p>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {sortedInstructions.map((instruction) => {
          const config = ROUTING_TYPE_CONFIG[instruction.type];
          const sticker = variant === "checkInRouting" ? STICKER_CALLOUT[instruction.type] : null;

          return (
            <div
              key={instruction.type}
              className={`${config.bgColor} ${config.borderColor} border-2 border-l-[6px] rounded-xl p-5`}
            >
              {/* Header */}
              <div className="flex items-center gap-3 mb-3">
                <div
                  className={`w-5 h-5 rounded-full ${config.dotColor} flex-shrink-0 shadow-[0_0_8px_2px] ${config.dotGlow}`}
                />
                <span className={`${config.badgeBg} ${config.badgeText} text-xs font-semibold px-3 py-1 rounded-full`}>
                  {config.label}
                </span>
                <span className="text-sm text-secondary-500">{instruction.totalUnits} units total</span>
              </div>

              {sticker && (
                <p
                  className={`text-xl md:text-xl font-extrabold uppercase tracking-tight ${sticker.colorTextClass} mb-3 leading-snug`}
                >
                  {sticker.colorLabel ? (
                    <>
                      Please put a <span className="underline">{sticker.colorLabel}</span> sticker on this box, it needs
                      to go into <span className="underline">Lane {sticker.lane}</span>.
                    </>
                  ) : (
                    <>
                      It needs to go into <span className="underline">Lane {sticker.lane}</span>.
                    </>
                  )}
                </p>
              )}

              {/* Instruction */}
              <p className={`${config.textColor} font-semibold text-sm mb-3`}>{instruction.instruction}</p>

              {/* Products */}
              <div className="flex flex-wrap gap-2">
                {instruction.products.map((product) => (
                  <span
                    key={product.id}
                    className={`${config.productBg} ${config.productBorder} border rounded-lg px-3 py-1.5 text-sm text-secondary-700`}
                  >
                    <span className="font-mono font-bold text-secondary-900">{product.skuName}</span>
                    <span className="text-secondary-400 mx-1.5">&mdash;</span>
                    {product.name} ({product.quantity} units)
                    {product.shortage != null && product.shortage > 0 && (
                      <span className="text-error-600 font-semibold ml-0.5">(-{product.shortage} short)</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { RoutingInstructionCards };
